import axios from 'axios';

// In production (Docker), use /api prefix which nginx proxies to backend
// In development, use the proxy from package.json or direct URL
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : '');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  // Send the session cookie. Same-origin in every environment (nginx in
  // production, CRA proxy in dev), so no CORS negotiation is involved.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Called by AuthContext so a 401 anywhere can drop the app back to the login
// screen without this module importing React.
let onUnauthorized = null;
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Remove Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // Add demo mode header if active
    if (localStorage.getItem('demoMode') === '1') {
      config.headers['X-Demo-Mode'] = '1';
    }
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error(`API Error: ${error.response?.status} ${error.config?.url}`, error.response?.data);
    // Session expired or never existed -> hand control back to the auth layer.
    // /auth/* responses are excluded so a failed login shows its own error
    // instead of tripping the global "you were logged out" path.
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.startsWith('/auth/') && onUnauthorized) {
      onUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Hydro Units API
export const hydroUnitsAPI = {
  // Get sensor data for a unit
  getSensors: (unitId) => api.get(`/units/${unitId}/sensors`),

  // Get relay states for a unit
  getRelays: (unitId) => api.get(`/units/${unitId}/relays`),

  // Relay state + control mode + schedule for every active unit in one call
  getRelaysOverview: () => api.get('/relays/overview'),

  // What the relay controllers report back: liveness, and whether the relay
  // actually moved. Distinct from getRelays, which is what the server commanded.
  getRelayDeviceStatus: () => api.get('/devices/relay-status'),
  // Afternoon heat breaks: every timer rack's lights off together when the front end is
  // dangerously hot. Returns the switch, the rules, today's breaks and the current decision.
  getHeatBreaks: () => api.get('/lights/heat-breaks'),
  setHeatBreaks: (enabled) => api.post('/lights/heat-breaks', { enabled }),

  // Update relay state (manual - switches to manual mode)
  updateRelay: (unitId, relayData) => api.post(`/units/${unitId}/relay`, relayData),

  // Get schedule for a unit
  getSchedule: (unitId) => api.get(`/units/${unitId}/schedule`),

  // Update schedule for a unit
  updateSchedule: (unitId, scheduleData) => api.post(`/units/${unitId}/schedule`, scheduleData),

  // Update control mode for a specific relay (manual/timer)
  updateControlMode: (unitId, relay, mode) => api.post(`/units/${unitId}/control_mode`, { relay, mode }),
};

// Room Monitoring API
export const roomAPI = {
  // Get front room sensors
  getFrontSensors: () => api.get('/room/front/sensors'),

  // Get back room sensors
  getBackSensors: () => api.get('/room/back/sensors'),

  // Get AC schedule
  getACSchedule: () => api.get('/room/back/ac_schedule'),

  // Dehumidifier, switched by the Pi agent on the farm LAN. A set is a command the
  // Pi picks up within ~15 s; the state shown is what the unit itself reported.
  // settings: any of { switch_on: bool, target_humidity: 25|30|40|50|60, fan: 'low'|'mid'|'high' }
  getDehumidifier: () => api.get('/dehumidifier'),
  setDehumidifier: (settings) => api.post('/dehumidifier', settings),
  // Auto humidity: {enabled: bool} or {resume: true} (ends a pause after a change by hand).
  setDehumidifierAuto: (body) => api.post('/dehumidifier/auto', body),

  // Update AC schedule
  updateACSchedule: (scheduleData) => api.post('/room/back/ac_schedule', scheduleData),

  // AC balancer: the combo the controller is currently being told to run
  getACTarget: () => api.get('/room/back/ac_target'),

  // AC balancer enable flag
  updateACControl: (payload) => api.post('/room/back/ac_control', payload),

  // Liveness + per-sensor health for the front/back room controllers
  getRoomDeviceStatus: () => api.get('/devices/room-status'),

  // Both ends of the container + AC setpoint for a range: '6h' | 'today' | '7d' | '30d'
  getClimate: (range = 'today') => api.get('/room/climate', { params: { range } }),

  // Today so far vs yesterday: time in range, swing, humidity, VPD, energy
  getClimateScore: () => api.get('/room/climate/score'),
};

// Outside weather API
export const weatherAPI = {
  // Latest outside conditions plus the inside comparison
  getCurrent: () => api.get('/weather/current'),

  // Outside weather history for charts (24h, 7d, 30d, 90d)
  getHistory: (range = '24h') => api.get('/weather/history', { params: { range } }),
};

// Power meter API — the Tuya 3-phase energy meter, fed by tuya_meter_agent.py
// running on the farm LAN. Every one of these reads from the real database, never
// demo.db, so they behave the same whether or not demo mode is on.
//
// Derived figures (total load, power factor, phase imbalance, staleness) are
// computed server-side in /meter/latest on purpose — recomputing them here would
// be a second implementation to keep in step with the first.
export const meterAPI = {
  // Live snapshot: per-phase readings, totals, meter state, freshness
  getLatest: () => api.get('/meter/latest'),

  // Per-phase history for one metric, pivoted to {timestamp, A, B, C} rows.
  // metric must be 'power_w' | 'voltage' | 'current' — the backend rejects others.
  getHistory: (metric = 'power_w', range = '24h') =>
    api.get('/meter/history', { params: { metric, range } }),

  // Consumption per IST day in kWh, derived from the cumulative meter total
  getEnergy: (range = '7d') => api.get('/meter/energy', { params: { range } }),

  // Cumulative units through the day for today, yesterday and a typical day,
  // plus today's peak / base load / voltage facts
  getDayProfile: () => api.get('/meter/day-profile'),

  // Agent-reported meter outages, newest first
  getEvents: (limit = 20) => api.get('/meter/events', { params: { limit } }),
};

// Manual sensor entry API — handheld-meter readings, a source independent of the
// probes. Never merge these values into the reservoir readings.
export const manualEntryAPI = {
  // Per-unit, per-sensor latest value + today/week aggregates, keyed { unitId: { ph, tds, ec } }
  getStats: () => api.get('/manual-entry/stats'),

  // Entries for one unit, newest first; optional sensor_type filter
  getEntries: (unitId, params = {}) => api.get(`/manual-entry/${unitId}`, { params }),

  // Record a reading. sensorType must be 'ph' | 'tds' | 'ec' — the backend rejects others.
  add: (unitId, sensorType, value, notes = '') =>
    api.post('/manual-entry', { unit_id: unitId, sensor_type: sensorType, value, notes }),
};

// pH test bench — pH / TDS / water temp typed on the pH Test page (or posted by a probe). Stored apart from every real
// reading: they never reach the tanks, the Dashboard or the WhatsApp group.
export const phTestAPI = {
  list: (params = {}) => api.get('/ph-test', { params }),
  remove: (id) => api.delete(`/ph-test/${id}`),
};

// Plant API — logs and derived plant statistics
export const plantsAPI = {
  // Latest log per plant, paginated (max limit 200 server-side)
  getLatest: (params = {}) => api.get('/plants/latest', { params }),

  // Archived (dead) plants with their last log and original position
  getDead: (limit = 200) => api.get('/plants/dead', { params: { limit } }),

  updateLog: (logId, data) => api.put(`/plant-log/${logId}`, data),

  transfer: (oldId, newId) => api.post('/plant-log/transfer', { old_plant_id: oldId, new_plant_id: newId }),
  markDead: (plantId, notes) => api.post('/plant-log/mark-dead', { plant_id: plantId, notes }),

  // Newest logs across every plant (each with its `images` filenames)
  getRecentLogs: (limit = 40) => api.get('/plant-log', { params: { limit } }),

  // The crop's statistics: time to bud, rack vs shelf, pace, health, tank pH, with intervals and tests
  getStats: () => api.get('/systems/stats'),

  // Each tank and spot: pH on target, pH Down, EC, nutrient, roots, deaths, position + climate
  getCompare: () => api.get('/systems/compare'),
};

// Buds / flowers taken off a plant. Not a check: no stage, health or roots.
export const plantRemovalsAPI = {
  list: (params = {}) => api.get('/plant-removals', { params }),
  add: ({ plantId, buds = 0, flowers = 0, notes = '' }) =>
    api.post('/plant-removals', { plant_id: plantId, buds, flowers, notes }),
  remove: (id) => api.delete(`/plant-removals/${id}`),
};

// Today's manager attendance + tasks, and spray products' next-due dates
export const farmLogAPI = {
  getManagerToday: () => api.get('/manager-log/today'),
  getSpraySchedule: () => api.get('/sprays/schedule'),
};

// WhatsApp group alerts: config, bot link status, and every watched device's online state
export const alertsAPI = {
  get: () => api.get('/alerts'),
};

// Calendar / schedule API
export const calendarAPI = {
  // Per-reservoir last change, next due and overdue flag
  getWaterSchedule: () => api.get('/calendar/water-schedule'),

  // Record a water change for one reservoir, at `when` (Unix ts) or now
  recordWaterChange: (systemId, when = null) =>
    api.post(`/nutrients/reservoirs/${systemId}`, { water_change: true, water_change_at: when }),
};

// Plant Meta API
export const plantMetaAPI = {
  // Projected stage date windows (best/average/worst) from this plant's transplant date
  getProjection: (plantId) => api.get(`/plants/${plantId}/projection`),
};

// Nutrients: products and dose logging. A dose with is_water_change false is a
// top-up or pH correction (Manual Entry); pH Up/Down carry no nutrient_id.
export const nutrientsAPI = {
  list: () => api.get('/nutrients'),
  logDose: (payload) => api.post('/nutrients/dose', payload),
  // Newest first; pass { system_id, limit }
  getDoses: (params = {}) => api.get('/nutrients/doses', { params }),
  // Volume, last water change and stage per reservoir
  getReservoirs: () => api.get('/nutrients/reservoirs'),
};

// Growing systems: when each was planted (the ground truth for "day N") and its
// crop progress. Set from the Calendar's Days Since Planting card.
export const systemsAPI = {
  getCrop: () => api.get('/systems/crop'),
  setPlanting: (systemId, plantedAt) => api.post(`/systems/${systemId}/planting`, { planted_at: plantedAt }),
};

// Settings API
export const settingsAPI = {
  // Get safe ranges
  getRanges: () => api.get('/settings/ranges'),
};

// Get API base URL for direct image URLs
export const getApiBaseUrl = () => API_BASE_URL;

// Daily rack photos. Photos go up one per request (already shrunk on the phone), so
// a batch never hits the server's upload cap and each gets its own progress.
export const rackPhotosAPI = {
  list: (params = {}) => api.get('/rack-photos', { params }),
  upload: (unitId, blob, note, onUploadProgress) => {
    const form = new FormData();
    form.append('unit_id', unitId);
    form.append('photo', blob, 'photo.jpg');
    if (note) form.append('note', note);
    return api.post('/rack-photos', form, { onUploadProgress, timeout: 120000 });
  },
  remove: (photoId) => api.delete(`/rack-photos/${photoId}`),
  // "Send to group": one WhatsApp link for the whole day, every rack on it.
  send: (day) => api.post('/rack-photos/send', day ? { day } : {}),
  // Public share link: no session needed, so it works for anyone in the WhatsApp group.
  shared: (token) => api.get(`/share/rack-photos/${token}`),
  // Server paths come back relative ('/rack-photos/thumb/...'); prefix the API base.
  src: (path) => `${API_BASE_URL}${path}`,
};

// Issues: farm problems tracked like tickets. Create / comment send multipart when
// photos come along (already shrunk on the phone), JSON otherwise.
const issueForm = (fields, photos) => {
  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    form.append(k, Array.isArray(v) || typeof v === 'object' ? JSON.stringify(v) : v);
  });
  (photos || []).forEach((blob, i) => form.append('images', blob, `photo${i}.jpg`));
  return form;
};

export const issuesAPI = {
  list: (params = {}) => api.get('/issues', { params }),
  get: (id) => api.get(`/issues/${id}`),
  create: (fields, photos, onUploadProgress) =>
    api.post('/issues', issueForm(fields, photos), { onUploadProgress, timeout: 120000 }),
  // Each changed field becomes its own entry in the issue's trail; `note` adds a comment.
  update: (id, changes) => api.patch(`/issues/${id}`, changes),
  comment: (id, fields, photos, onUploadProgress) =>
    api.post(`/issues/${id}/events`, issueForm(fields, photos), { onUploadProgress, timeout: 120000 }),
  // Server paths come back relative ('/issues/thumb/...'); prefix the API base.
  src: (path) => `${API_BASE_URL}${path}`,
};

// Purchase requests: what the farm needs bought, requested → approved → ordered → received.
export const purchasesAPI = {
  list: (status = 'open') => api.get('/purchases', { params: { status } }),
  create: (fields) => api.post('/purchases', fields),
  // `status` changes go on the trail (with `note`; required to decline).
  update: (id, changes) => api.patch(`/purchases/${id}`, changes),
};

// Activity log: every write by a signed-in account (admins only)
export const auditAPI = {
  list: (params = {}) => api.get('/audit-log', { params }),
};

// AI Chat API
export const chatAPI = {
  // The cost meter on Settings: tokens priced per model, today / 7 days / month / all time.
  usage: () => api.get('/ai/usage'),
  /* The assistant's streamed answer. axios cannot read a response body as it arrives
   * in the browser, so this one call uses fetch, with the same base URL, session
   * cookie and demo header as the axios instance above.
   *
   * The body is NDJSON, one event per line: {type: 'status' | 'text' | 'images' |
   * 'done' | 'error', ...}. Each parsed event goes to onEvent in order. Errors before
   * the stream starts (400 / 401 / 503) come back as JSON {error} and are thrown with
   * `.status` set. A plain JSON answer ({response, images}, the pre-streaming shape)
   * is replayed as events so either backend works. Resolves once the body ends. */
  streamMessage: async (message, history = [], { signal, onEvent, issueId } = {}) => {
    const headers = { 'Content-Type': 'application/json', Accept: 'application/x-ndjson' };
    if (localStorage.getItem('demoMode') === '1') headers['X-Demo-Mode'] = '1';

    const res = await fetch(`${API_BASE_URL}/ai/chat`, {
      method: 'POST',
      credentials: 'include',
      headers,
      // issue_id: an issue tagged in the bubble; the server reads it and every photo on it
      // and answers with a 'context' event the caller keeps for follow-ups.
      body: JSON.stringify({ message, history, ...(issueId != null && { issue_id: issueId }) }),
      signal,
    });

    if (!res.ok) {
      let detail = null;
      try {
        const data = await res.json();
        detail = data.error || data.message || null;
      } catch (e) { /* not JSON: nginx error page, proxy timeout */ }
      if (res.status === 401 && onUnauthorized) onUnauthorized();
      const err = new Error(detail || `The assistant answered with HTTP ${res.status}.`);
      err.status = res.status;
      err.detail = detail;
      throw err;
    }

    const emit = (event) => { if (event && typeof event === 'object' && onEvent) onEvent(event); };
    const type = res.headers.get('content-type') || '';

    if (!type.includes('ndjson') || !res.body) {
      const data = await res.json();
      if (data.error) { emit({ type: 'error', error: data.error }); return; }
      if (Array.isArray(data.images) && data.images.length) {
        emit({ type: 'images', images: data.images.map(i => ({ thumb: i.thumb || i.url, full: i.full || i.url })) });
      }
      emit({ type: 'text', delta: data.response || '' });
      emit({ type: 'done', model: data.model });
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    const flushLines = (final) => {
      const lines = buffer.split('\n');
      buffer = final ? '' : lines.pop();
      lines.forEach((line) => {
        const text = line.trim();
        if (!text) return;
        try { emit(JSON.parse(text)); } catch (e) { console.warn('Chat: skipped a line that is not JSON', text.slice(0, 120)); }
      });
    };
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      flushLines(false);
    }
    buffer += decoder.decode();
    flushLines(true);
  },
};

// Utility functions
export const apiUtils = {
  // Handle API errors
  handleError: (error, defaultMessage = 'An error occurred') => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return defaultMessage;
  },
};

export default api;